import { to } from "@lib/utils";
import { AppError } from "@models/error";
import { NFTEvent, NFTEventParam } from "@models/event";
import { NFTConstraint } from "@models/event/constraint/nft";
import { translate } from "@models/language";
import {
  Discount,
  DiscountParam,
  Transaction,
  TransactionParam,
  TransactionStatus,
} from "@models/payment";
import { apiClient, logOut, useUserStore } from "@providers/auth";
import { isEmpty } from "lodash";
import { create } from "zustand";
export { formatPrice } from "@models/currency";
interface PaymentStore {
  event: NFTEvent | null;
  transaction: Transaction | null;
  discount: Discount | null;
  currentStep: PaymentStep;
  allowSubmit: boolean;
}

export enum PaymentStep {
  SELECT_ITEMS,
  CHECKOUT,
  COMPLETE,
}

export const usePaymentStore = create<PaymentStore>((set) => ({
  event: null,
  transaction: null,
  discount: null,
  currentStep: PaymentStep.SELECT_ITEMS,
  allowSubmit: false,
}));

export const resetPaymentStore = () => {
  usePaymentStore.setState({
    event: null,
    transaction: null,
    discount: null,
    currentStep: PaymentStep.SELECT_ITEMS,
    allowSubmit: false,
  });
};

export const getEventByIdOrSlug = async (
  eventIdOrSlug: string,
  publishedOnly = true
): Promise<NFTEvent | null> => {
  const event = usePaymentStore.getState().event;
  if (event) return event;
  const eventParams: NFTEventParam = await apiClient.get(
    `/events/${eventIdOrSlug}${publishedOnly ? "?published=true" : ""}`
  );
  if (!eventParams) return null;
  const _event = new NFTEvent(eventParams);
  usePaymentStore.setState({ event: _event });
  return _event;
};

export const getTransactionById = async (transactionId: string) => {
  const event = usePaymentStore.getState().event;
  const user = useUserStore.getState().user;
  //console.log("getTransactionById", userId, eventId, transactionId);
  if (!event || !event?.id) return null;

  let _transaction: Transaction | null = null;
  _transaction = usePaymentStore.getState().transaction;
  if (!_transaction) {
    const transactionParams: TransactionParam = await apiClient.get(
      `/transactions/${transactionId}`
    );
    if (transactionParams?.id === transactionId) {
      _transaction = new Transaction({
        ...transactionParams,
        organizerId: event.organizerId,
        eventName: translate(event.info).name,
      });
    } else {
      _transaction = new Transaction({
        eventId: event.id,
        id: transactionId,
        userId: user?.id || "",
        organizerId: event.organizerId,
        eventName: translate(event.info).name,
      });
    }
  }
  if (event.id) _transaction.eventId = event.id;
  if (event.transactionFee) _transaction.fee = event.transactionFee;
  if (event.currency) _transaction.currency = event.currency;
  if (user?.id && !_transaction.userId) _transaction.userId = user.id;
  if (user?.email && !_transaction.contact.email)
    _transaction.contact.email = user.email;

  usePaymentStore.setState({
    transaction: _transaction,
    discount: _transaction.discount || null,
  });
  return _transaction;
};

export const saveTransaction = async (transaction: Transaction) => {
  if (!transaction?.id) return false;
  const event = usePaymentStore.getState().event;
  const user = useUserStore.getState().user;
  if (event?.id) transaction.eventId = event.id;
  if (user) {
    if (user.id) transaction.userId = user.id;
    if (user.email) transaction.contact.email = user.email;
    if (!isEmpty(user.evmWallets))
      transaction.contact.wallet = user.evmWallets[0];
  }
  return apiClient.post("/transactions", transaction.json, true);
};

export const validateDiscountCode = async (eventId: string, code: string) => {
  const { res: discount } = await to(
    apiClient.get(
      `/discounts?eventId=${eventId}&code=${encodeURIComponent(code)}`
    )
  );
  if (!discount?.code) return null;
  return new Discount(discount as DiscountParam);
};

export const checkoutPayment = async (transaction: Transaction) => {
  const event = usePaymentStore.getState().event;
  const user = useUserStore.getState().user;
  if (!event || !user || transaction.status === TransactionStatus.SUCCEEDED)
    throw new Error(AppError.UNKNOWN);
  transaction.userId = user.id;
  transaction.eventId = event.id;
  if (!transaction.contact.email) transaction.contact.email = user.email;
  if (!isEmpty(user.evmWallets)) {
    const userWallet = user.evmWallets[0];
    if (!transaction.contact.wallet) transaction.contact.wallet = userWallet;
    // const n = transaction.items.length;
    // for (let i = 0; i < n; i++) {
    //   const _item = transaction.items[i];
    //   if (_item.contact.wallet) continue;
    //   if (compareText(_item.contact.email || "", user.email))
    //     transaction.items[i].contact.wallet = userWallet;
    // }
  }
  const baseURL = `${window.location.origin}${window.location.pathname}`;
  const successUrl = `${baseURL}/success`;
  const cancelUrl = `${baseURL}/cancel`;
  const url = await apiClient.post(
    "/transactions/checkout",
    { transaction, successUrl, cancelUrl },
    true
  );
  console.log("checkoutPayment", url);
  return url;
};

export const resetTransaction = async (transactionId: string) => {
  return apiClient.patch(`/transactions/${transactionId}/initial`);
};

// export const submitTransaction = async (transactionId: string) => {
//   return apiClient.patch(`/transactions/${transactionId}/submitted`);
// };

export const cancelTransaction = async (transactionId: string) => {
  return apiClient.patch(`/transactions/${transactionId}/failed`, {
    error: "cancelled",
  });
};

export const validateNFTConstraint = async (
  userId: string,
  nftConstraint: NFTConstraint
) => {
  console.log("validateNFTConstraint", userId, nftConstraint);
  const { res } = await to(
    apiClient.post(`/verification/nft/user/${userId}`, nftConstraint.json)
  );
  console.log("validateNFTConstraint", res);
  return Number(res?.quantity || 0);
};

export const validatePreviousTransactions = async (
  eventId: string,
  productId: string,
  identifiers: string[],
  limit: number
) => {
  console.log("validatePreviousTransactions", identifiers);
  const { res } = await to(
    apiClient.post(`/transactions/verify`, { eventId, productId, identifiers })
  );
  console.log("validatePreviousTransactions", res, limit);
  if (isEmpty(res)) return true;
  return (res || []).length < limit;
};

export const resetCheckout = () => {
  window.location.href = window.location.href.replace(
    /\/checkout.*/,
    "/checkout"
  );
  return;
};

export const backToEvent = () => {
  window.location.href = window.location.href.replace(/\/checkout.*/, "");
  return;
};

export const handleLogout = () => {
  logOut();
  backToEvent();
  return;
};
